import { Link, NavLink, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  CheckSquare,
  ScanSearch,
  CalendarDays,
  Workflow,
  Megaphone,
  MessageSquare,
  Package,
  Users,
  UsersRound,
  Type,
  BadgePercent,
  Settings,
  Home,
  ShieldAlert,
  ArrowLeft,
  BookOpen,
  // PenLine, // hidden 2026-06-22 with the Composer nav item
  PackageCheck,
  Globe,
  Languages,
  FileCode2,
  ChevronDown,
  ChevronRight,
  MessageCircle,
  History,
  Mail,
  Menu,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { competitorEmailsApi } from "@/lib/api/competitorEmails";
import type { ChannelPlatform } from "@/lib/types";
import { useAuth } from "@/auth/AuthContext";
import { useAccess } from "@/auth/AccessContext";
import { cn } from "@/lib/cn";
import { IconBadge } from "@/components/IconBadge";
import type { IconTint } from "@/components/IconBadge";
import { Wordmark } from "@/components/Wordmark";
import { BrandSelector } from "@/components/BrandSelector";
import { OrgPicker } from "@/components/OrgPicker";
import { BrandAvatar } from "@/components/BrandAvatar";
import { RegionPicker } from "@/components/RegionPicker";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { UpdateNotice } from "@/components/UpdateNotice";
import { ChatPanel } from "@/components/ChatPanel";
import { useRegion } from "@/lib/useRegion";
import "@/components/competitor-emails/library.css";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  tint?: IconTint;
  /** Optional: only shown when at least one of these channels is connected. */
  channels?: ChannelPlatform[];
  /** Optional: gates by org role hierarchy. */
  minRole?: "creative" | "viewer" | "analyst" | "admin" | "owner";
  /** Optional: internal-only surface — visible to RetentionIQ superadmins only. */
  superAdminOnly?: boolean;
  /** Optional: hide from the nav for EVERYONE (incl. superadmins). Park a surface
   *  that isn't customer-ready; the route/page stays reachable by direct URL. */
  hidden?: boolean;
}

const ROLE_RANK: Record<string, number> = {
  creative: 0,
  viewer: 1,
  analyst: 2,
  admin: 3,
  owner: 4,
};

/**
 * Each group carries a stable `id` (used as the localStorage collapse key)
 * and an optional `heading`. Headingless groups render flat (no toggle).
 *
 * Grouping taxonomy (2026-06-01) — ported the ads-iq "named sections of
 * 2–6 items each" pattern. The old layout had 5 ungrouped top items and a
 * 10-item "Insights" dumping ground; this splits it into 7 tight groups.
 */
type NavGroup = { id: string; heading?: string; items: NavItem[]; requiresSms?: boolean };

const BRAND_NAV: NavGroup[] = [
  {
    id: "overview",
    heading: "Overview",
    items: [
      { to: "dashboard", label: "Overview", icon: LayoutDashboard, tint: "scale" },
      // retention divergence (user-approved): ads-iq has no left-nav Chat item.
      // RetentionIQ adds one linking to the full-page chat at /brand/:slug/chat
      // (the FloatingChat widget's "Open in full view" target).
      { to: "chat", label: "Advisor", icon: MessageCircle, tint: "warm" },
      { to: "weekly-review", label: "Weekly Review", icon: CalendarDays, tint: "warm" },
    ],
  },
  {
    id: "optimize",
    heading: "Optimize",
    items: [
      { to: "actions", label: "Actions", icon: CheckSquare, tint: "scale" },
      { to: "audit", label: "Audit", icon: ScanSearch, tint: "insight" },
      // Monitors hidden for now (2026-06-18) — Auto-monitors card is an
      // unbuilt stub; re-enable once auto-detection + custom monitors are
      // worth surfacing. Route still mounted in App.tsx (reachable by URL).
      // { to: "monitors", label: "Monitors", icon: Bell, tint: "insight" },
    ],
  },
  {
    id: "klaviyo",
    // Heading is an action-mode verb ("Engage") to match its siblings
    // (Overview / Optimize / Build) — these are the live, running programs and
    // the audiences they reach. NOT a channel name ("Email"): Flows & Campaigns
    // here carry BOTH email and SMS behind an in-page Email|SMS filter, so a
    // channel label would mislabel their contents. NOT a domain noun
    // ("Marketing"): the whole product is marketing, so that wouldn't exclude
    // its siblings. The standalone SMS group below is a distinct SMS *console*
    // (provider/billing/economics/coverage), not a duplicate of the filter, so
    // it stays a peer. id kept as "klaviyo" (localStorage collapse key + tint
    // token). Renamed 2026-06-22.
    heading: "Engage",
    items: [
      { to: "flows", label: "Flows", icon: Workflow, tint: "klaviyo", channels: ["klaviyo"] },
      { to: "campaigns", label: "Campaigns", icon: Megaphone, tint: "klaviyo", channels: ["klaviyo"] },
      // Templates hidden for now (2026-06-18) — non-load-bearing read-only
      // mirror of Klaviyo (mostly auto-generated "Untitled" snapshots); the
      // assembly flow uses template_slot_maps, not this list. Route still
      // mounted in App.tsx. Re-enable after the "sharpen" pass if wanted.
      // { to: "templates", label: "Templates", icon: LayoutTemplate, tint: "klaviyo", channels: ["klaviyo"] },
      { to: "translate", label: "Translate", icon: Languages, tint: "klaviyo", channels: ["klaviyo"] },
      { to: "segments", label: "Segments", icon: Users, tint: "klaviyo", channels: ["klaviyo"] },
    ],
  },
  {
    id: "sms",
    heading: "SMS",
    requiresSms: true,
    items: [
      { to: "sms", label: "SMS", icon: MessageSquare, tint: "sms" },
    ],
  },
  {
    id: "build",
    heading: "Build",
    items: [
      { to: "playbooks", label: "Playbooks", icon: BookOpen, tint: "warm" },
      { to: "import-html", label: "Import HTML email", icon: FileCode2, tint: "klaviyo", channels: ["klaviyo"] },
      // Hidden for now (2026-06-22): Composer page. Restore by uncommenting
      // here + the route/import in App.tsx.
      // { to: "composer", label: "Composer", icon: PenLine, tint: "klaviyo" },
    ],
  },
  {
    id: "experiments",
    heading: "Experiments",
    items: [
      { to: "subjects", label: "Copy Lab", icon: Type, tint: "subject" },
      { to: "promotions", label: "Promotions", icon: BadgePercent, tint: "warm" },
    ],
  },
  // Insights group retired 2026-06-18 — Performance + Reports folded into the
  // dashboard (per-flow/campaign breakdown + cross-channel attribution donut).
  // Impact + Synthesis were already hidden/parked and stay reachable by URL.
  {
    id: "data",
    heading: "Data",
    items: [
      { to: "customers", label: "Customers", icon: UsersRound, tint: "insight" },
      { to: "catalog", label: "Catalog", icon: Package, tint: "subject" },
      { to: "competitor-emails", label: "Competitor Emails", icon: Mail, tint: "subject" },
      { to: "next-order", label: "Next Order", icon: PackageCheck, tint: "scale" },
    ],
  },
  {
    id: "admin",
    heading: "Admin",
    items: [
      { to: "audit-log", label: "Audit Log", icon: History, tint: "insight" },
      { to: "settings", label: "Settings", icon: Settings, minRole: "admin" },
    ],
  },
];

const linkClasses = (isActive: boolean) =>
  cn(
    "relative flex items-center gap-2.5 mx-2 px-3 py-2.5 mb-0.5 text-sm rounded-md transition-colors",
    isActive
      ? "text-text-primary font-medium bg-accent-muted"
      : "text-text-secondary hover:bg-bg-hover",
  );

// Collapsible-nav-section state, ported from ads-iq. Persists per-section
// collapse to localStorage; auto-collapses any section with >= 6 items on
// first visit (no group currently hits this, so all default expanded).
const COLLAPSE_STORAGE_PREFIX = "retentioniq:nav:collapsed:";
const AUTO_COLLAPSE_THRESHOLD = 6;

function useSectionCollapsed(sectionId: string, itemCount: number) {
  const storageKey = COLLAPSE_STORAGE_PREFIX + sectionId;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(storageKey);
    if (stored !== null) return stored === "1";
    return itemCount >= AUTO_COLLAPSE_THRESHOLD;
  });
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        /* storage disabled / quota — collapse still works in-session */
      }
      return next;
    });
  }, [storageKey]);
  return { collapsed, toggle };
}

function BrandNavLink({ item, slug }: { item: NavItem; slug: string }) {
  return (
    <NavLink
      to={`/brand/${slug}/${item.to}`}
      end
      className={({ isActive }) => linkClasses(isActive)}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-accent rounded-r" />
          )}
          <IconBadge icon={item.icon} tint={item.tint} />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

function BrandNavSection({ group, slug }: { group: NavGroup; slug: string }) {
  const { collapsed, toggle } = useSectionCollapsed(group.id, group.items.length);

  // Defensive: a headingless group renders flat with no toggle.
  if (!group.heading) {
    return (
      <div className="space-y-0.5 mb-3">
        {group.items.map((item) => (
          <BrandNavLink key={item.to} item={item} slug={slug} />
        ))}
      </div>
    );
  }

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center gap-1.5 px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim hover:text-text-secondary transition-colors"
      >
        {collapsed ? (
          <ChevronRight size={11} strokeWidth={2.5} />
        ) : (
          <ChevronDown size={11} strokeWidth={2.5} />
        )}
        <span>{group.heading}</span>
        <span className="ml-auto tabular-nums font-medium text-text-dim/70">
          {group.items.length}
        </span>
      </button>
      {!collapsed && (
        <div className="space-y-0.5">
          {group.items.map((item) => (
            <BrandNavLink key={item.to} item={item} slug={slug} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { slug } = useParams();
  const { me, logout } = useAuth();
  const { isSuperAdmin, isOrgAdmin } = useAccess();

  // retention adaptation: compute `embedded` BEFORE the shell-only queries so
  // it can gate them (it used to be derived later, at the `useLocation`-based
  // computation below, AFTER these queries ran). Derived from the URL directly
  // (not a hook) so hook order stays stable; mirrors ads-iq AppShell, where the
  // shell-only queries are `enabled: !embedded` so the FloatingChat iframe
  // (?embedded=1) doesn't issue the brand/setup fetches the host shell already
  // owns. The embedded early-return short-circuit stays where it is below.
  const embedded =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embedded") === "1";

  const { data: brand } = useQuery({
    queryKey: ["brand", slug],
    queryFn: () => (slug ? api.getBrand(slug) : Promise.resolve(null)),
    enabled: !!slug && !embedded, // retention adaptation: skip in the chat iframe
  });

  // Only probe the competitor-emails router once the brand payload confirms the
  // feature is ON for this org. That router 404s by design when the flag is off
  // (the production default), and a browser logs a resource-load 404 on every
  // page if we probe it regardless — which is exactly what the canary smoke
  // rejected. Gating on brand.features.competitor_emails means the probe never
  // fires when off; navVisible still keys on the query's success (below), so the
  // nav item stays hidden until a real 200 comes back. (bugfix #442)
  const competitorLibrary = useQuery({
    queryKey: ["competitor-emails", slug, "brands"],
    queryFn: () => competitorEmailsApi.brands(slug!),
    enabled: !!slug && !embedded && brand?.features?.competitor_emails === true,
    retry: false,
    staleTime: 60_000,
  });

  const role = me?.org.role ?? "viewer";
  const connectedPlatforms = new Set<ChannelPlatform>(
    (brand?.connected_accounts ?? []).map((a) => a.platform),
  );
  // SMS-section visibility keys off whether the brand actually *sends* SMS
  // (provider-agnostic), distinct from the connected-platform `channels` gating.
  const brandSendsSms = (brand?.message_channels ?? []).includes("sms");

  const { region, setRegion, activeRegions, multiRegion } = useRegion(brand ?? null);
  const navigate = useNavigate();
  const location = useLocation();
  const onCompetitorPage = location.pathname.endsWith("/competitor-emails");
  const onActionDetailPage = /\/actions\/[^/]+$/.test(location.pathname);
  const compactMobileShell = onCompetitorPage || onActionDetailPage;
  const [libraryNavOpen, setLibraryNavOpen] = useState(false);
  useEffect(() => setLibraryNavOpen(false), [location.pathname]);

  // Embedded-chat entity-link receiver — ported 1:1 from ads-iq AppShell.tsx.
  // The FloatingChat iframe posts `{source:"retentioniq", type:"open-entity",
  // url}` when an operator clicks a flow/campaign/segment name in chat (see
  // useEntityLinker `open`). The HOST shell — NOT the embedded iframe instance —
  // drives the navigation so the drill opens on the page BEHIND the chat.
  // Gated `if (embedded) return;` so only the top/host AppShell listens (the
  // in-iframe AppShell instance never registers a listener → no double-handling
  // or loops). Same-origin + source + type + url-prefix guarded, matching ads-iq.
  // retention adaptation: `source` is "retentioniq" (this app's namespace), not
  // ads-iq's "adsiq"; `type`/`url` and the `/brand/` prefix check are identical.
  useEffect(() => {
    if (embedded) return;
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { source?: string; type?: string; url?: string } | null;
      if (!d || d.source !== "retentioniq" || d.type !== "open-entity") return;
      if (typeof d.url !== "string" || !d.url.startsWith("/brand/")) return;
      navigate(d.url);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedded, navigate]);

  // Embedded mode = this AppShell instance is running INSIDE the FloatingChat
  // iframe (/brand/:slug/chat?embedded=1). Mirrors ads-iq AppShell.tsx: the
  // embedded branch renders just the chat (no sidebar/topbar/banners and no
  // recursive ChatPanel) so the chat fills the iframe instead of being wrapped
  // in another full shell. Computed once near the top of the component (it now
  // also gates the shell-only brand/setup queries) — see definition above.

  // Suppress the FloatingChat widget on the dedicated full-page chat route —
  // the full page already owns the screen there (mirrors ads-iq's
  // `!location.pathname.endsWith("/chat")` guard).
  const onChatPage = location.pathname.endsWith("/chat");

  // "All markets" IS the Overview route. The picker shows "All" only while on
  // /overview; everywhere else it shows the active single market (primary by
  // default). Selecting "All" routes to the Overview; selecting a market from
  // the Overview drops into that market's dashboard.
  const onOverview = !!slug && location.pathname.startsWith(`/brand/${slug}/overview`);
  const pickerRegion = onOverview ? null : region;
  function handleRegionChange(next: string | null) {
    if (!next) {
      if (slug) navigate(`/brand/${slug}/overview`);
      return;
    }
    if (onOverview && slug) {
      navigate(`/brand/${slug}/dashboard?region=${encodeURIComponent(next)}`);
    } else {
      setRegion(next);
    }
  }

  function navVisible(item: NavItem): boolean {
    if (item.hidden) return false;
    if (item.to === "competitor-emails" && !competitorLibrary.isSuccess) return false;
    if (item.to === "import-html" && brand?.features?.html_import !== true) return false;
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.channels && !item.channels.some((c) => connectedPlatforms.has(c))) return false;
    if (item.minRole && ROLE_RANK[role] < ROLE_RANK[item.minRole]) return false;
    return true;
  }

  // Embedded short-circuit — render only the page body (the iframed Chat),
  // no sidebar/topbar/banners and no recursive ChatPanel. Mirrors ads-iq's
  // embedded branch. UpdateNotice stays mounted so the iframe still polls
  // /api/version and can prompt a refresh on a stale bundle.
  if (embedded) {
    return (
      <main className="h-screen w-screen overflow-hidden p-3">
        <UpdateNotice />
        <Outlet />
      </main>
    );
  }

  return (
    <div className={cn("h-screen overflow-hidden bg-bg flex", compactMobileShell && "ce-shell")}>
      {/* Sidebar — fixed full-height column; only its <nav> scrolls, so the
          sidebar stays put while the main content area scrolls independently. */}
      <aside id="app-navigation" data-open={libraryNavOpen} className="w-60 shrink-0 h-screen bg-bg-card border-r border-border flex flex-col">
        <div className="px-4 py-3.5 border-b border-border-lighter">
          <Link to="/dashboard" className="block">
            <Wordmark size="md" />
          </Link>
          {slug && me && me.brands.length > 1 ? (
            <div className="mt-2">
              <BrandSelector />
            </div>
          ) : (
            brand && (
              <div className="mt-2 flex items-center gap-2">
                <BrandAvatar name={brand.name} slug={brand.slug} size="sm" />
                <div className="text-xs text-text-secondary truncate">{brand.name}</div>
              </div>
            )
          )}
          {slug && multiRegion && (
            <div className="mt-2">
              <RegionPicker
                region={pickerRegion}
                options={activeRegions}
                onChange={handleRegionChange}
              />
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {!slug && (
            <div className="space-y-0.5">
              <NavLink to="/dashboard" className={({ isActive }) => linkClasses(isActive)}>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-accent rounded-r" />
                    )}
                    <IconBadge icon={Home} tint="scale" />
                    Portfolio
                  </>
                )}
              </NavLink>
              {/* Org settings + Admin console moved to the always-visible
                  account area at the sidebar footer (ads-iq parity). */}
            </div>
          )}

          {slug && (
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-1.5 mb-2 text-xs text-text-muted hover:text-text-primary hover:bg-bg-hover"
            >
              <ArrowLeft size={12} strokeWidth={2} />
              All brands
            </Link>
          )}

          {/* Executive Overview — top brand-nav entry, multi-region only.
              The cross-market landing page; single-region brands never see it
              (they land on Dashboard). */}
          {slug && multiRegion && (
            <div className="space-y-0.5 mb-3">
              <NavLink
                to={`/brand/${slug}/overview`}
                end
                className={({ isActive }) => linkClasses(isActive)}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-accent rounded-r" />
                    )}
                    <IconBadge icon={Globe} tint="warm" />
                    All Markets
                  </>
                )}
              </NavLink>
            </div>
          )}

          {slug &&
            BRAND_NAV.map((group) => {
              if (group.requiresSms && !brandSendsSms) return null;
              const visibleItems = group.items.filter(navVisible);
              if (visibleItems.length === 0) return null;
              return (
                <BrandNavSection
                  key={group.id}
                  group={{ ...group, items: visibleItems }}
                  slug={slug}
                />
              );
            })}
        </nav>

        {/* Account area (ads-iq parity): gated admin links → org switcher →
            user identity + sign out. */}
        {(isOrgAdmin || isSuperAdmin) && (
          <div className="border-t border-border-lighter p-2 space-y-0.5">
            {isOrgAdmin && (
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded text-sm font-medium transition-colors",
                    isActive
                      ? "bg-bg-subtle text-text-primary"
                      : "text-text-secondary hover:bg-bg-hover hover:text-text-primary",
                  )
                }
              >
                <Settings size={15} strokeWidth={1.75} />
                Org settings
              </NavLink>
            )}
            {isSuperAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded text-sm font-medium transition-colors",
                    isActive
                      ? "bg-amber-50 text-amber-900"
                      : "text-amber-700 hover:bg-amber-50 hover:text-amber-900",
                  )
                }
              >
                <ShieldAlert size={15} strokeWidth={1.75} />
                Admin console
              </NavLink>
            )}
          </div>
        )}

        <div className="border-t border-border-lighter p-2">
          <OrgPicker />
        </div>

        {/* User identity + sign out */}
        <div className="border-t border-border-lighter p-3 flex items-center gap-2">
          <div className="size-8 rounded-full bg-bg-subtle flex items-center justify-center text-sm font-semibold text-text-secondary shrink-0">
            {me?.user.email?.[0]?.toUpperCase() ?? "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-text-primary truncate">
              {me?.user.email ?? "—"}
            </div>
            {/* "free" is the unset default every org has today, not a real plan,
                so only a set tier is shown. */}
            {me?.org.tier && me.org.tier !== "free" && (
              <div className="text-[11px] text-text-muted capitalize">{me.org.tier}</div>
            )}
          </div>
          <button
            onClick={() => void logout()}
            title="Sign out"
            aria-label="Sign out"
            className="p-1.5 rounded-md text-text-muted hover:bg-bg-hover hover:text-text-primary shrink-0"
          >
            <LogOut size={15} strokeWidth={1.75} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        {compactMobileShell && <div className="ce-mobile-appbar"><button aria-label={libraryNavOpen ? "Close app navigation" : "Open app navigation"} aria-expanded={libraryNavOpen} aria-controls="app-navigation" onClick={() => setLibraryNavOpen(!libraryNavOpen)}>{libraryNavOpen ? <X size={18} /> : <Menu size={18} />}<span>RetentionIQ</span></button></div>}
        {compactMobileShell && libraryNavOpen && <button className="ce-mobile-nav-backdrop" aria-label="Close navigation backdrop" onClick={() => setLibraryNavOpen(false)} />}
        <ImpersonationBanner />
        <Outlet />
      </main>

      {/* Chat advisor — Plan 6. Visible only when slug param present;
          self-renders bottom-right inside the main column. Suppressed on the
          dedicated /chat full-page route (mirrors ads-iq) so the widget and
          the full page don't both render. */}
      {!onChatPage && <ChatPanel />}

      {/* Update notice — modal renders only when version mismatch detected */}
      <UpdateNotice />
    </div>
  );
}
